import {
  useCallback,
  ReactNode,
  ComponentType,
  Fragment,
  useRef,
  useState,
  useEffect,
} from 'react';
import { useLocation } from 'react-router-dom';

import { ScrollToElementStyled } from './scroll-to-on-mount.styles';
import { LAYOUT_HEADER_HEIGHT } from 'approot/shared/layout/layout.styles';
import { CONTENT_PAGE_ACTION_BAR } from 'approot/shared/layout/layout.constants';
import { useRegionData } from 'approot/shared/region/region.apollo';

type Props = {
  children: ReactNode;
  hashPattern: string;
  as?: keyof JSX.IntrinsicElements | ComponentType<any>;
  onMatch?: () => void;
} & Omit<JSX.IntrinsicElements['div'], 'ref' | 'tabIndex'>;

function ScrollToOnMount({
  children,
  hashPattern,
  as,
  onMatch,
  ...props
}: Props) {
  const location = useLocation();
  const [currentHash, setCurrentHash] = useState<string>(location.hash);
  const containerRef = useRef<HTMLDivElement>();
  const {
    regionBanner: { height },
  } = useRegionData();

  // Listen to location changes to scroll in case the hash link is on the same page
  useEffect(() => {
    setCurrentHash(location.hash);
  }, [location.hash]);

  const hashMatches = !!currentHash.match(hashPattern);

  const scroll = useCallback(() => {
    setTimeout(() => {
      if (!hashMatches || !containerRef.current) return;
      const actionBar = document.getElementById(CONTENT_PAGE_ACTION_BAR);
      const rect = containerRef.current.getBoundingClientRect();
      // get the offset from the element to the viewport
      const fromTop =
        (window.pageYOffset || document.documentElement.scrollTop) -
        (LAYOUT_HEADER_HEIGHT +
          (actionBar ? actionBar.getBoundingClientRect().height - 2 : 0)) +
        rect?.top -
        height;

      window.scrollTo(0, fromTop);
    }, 400);
  }, [hashMatches, height]);

  const setRefAndScroll = useCallback(
    (node: HTMLDivElement) => {
      containerRef.current = node;
      if (onMatch && hashMatches) {
        onMatch();
      }
      scroll();
    },
    [scroll, onMatch, hashMatches]
  );

  if (!hashMatches) return <Fragment>{children}</Fragment>;

  return (
    <ScrollToElementStyled
      tabIndex={-1}
      ref={setRefAndScroll}
      as={as}
      {...props}
    >
      {children}
    </ScrollToElementStyled>
  );
}

export default ScrollToOnMount;
